import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import {
  getChartDataForKpi,
  getChartsForProject,
} from '@/features/charts/api/get-charts-for-project';
import { ChartConfigWithPopulatedData } from '@/features/charts/api/types';
import { isDerivativeKpi } from '@/features/projects/shared/utils/kpi-utils';
import {
  KpiWithEntries,
  KpiWithMetrics,
  ProjectType,
} from '@/shared/types/api';

type Props = UseQueryOptions<
  ChartConfigWithPopulatedData[],
  unknown,
  ChartConfigWithPopulatedData[]
>;

export const chartConfigsQueryKey = (projectId?: number) => [
  'chartConfigs',
  projectId,
];

export const useChartConfigsForProject = ({
  id,
  projectType,
  props,
}: {
  id: number;
  projectType?: ProjectType;
  props?: Props;
}) => {
  return useQuery({
    ...props,
    queryKey: chartConfigsQueryKey(id),
    queryFn: async () => {
      return getChartsForProject(id, projectType);
    },
  });
};

export const chartDataForKpiQueryKey = (
  kpiId?: number,
  projectId?: number,
  monthsBack?: number,
  isDerivative?: boolean,
  fromDate?: Date,
  toDate?: Date,
) => [
  'chartDataForKpi',
  kpiId,
  projectId,
  monthsBack,
  isDerivative,
  fromDate?.getTime(),
  toDate?.getTime(),
];

type ChartDataForKpiProps = UseQueryOptions<
  KpiWithEntries,
  unknown,
  KpiWithEntries
>;

export const useChartDataForKpi = ({
  id,
  projectId,
  monthsBack,
  kpi,
  fromDate,
  toDate,
  props,
}: {
  id: number;
  projectId: number | undefined;
  monthsBack?: number;
  kpi?: KpiWithMetrics;
  fromDate?: Date;
  toDate?: Date;
  props?: ChartDataForKpiProps;
}) => {
  const isDerivative = kpi ? isDerivativeKpi(kpi) : undefined;

  return useQuery({
    ...props,
    queryKey: chartDataForKpiQueryKey(
      id,
      projectId,
      monthsBack,
      isDerivative,
      fromDate,
      toDate,
    ),
    queryFn: async () =>
      getChartDataForKpi(
        id,
        projectId!,
        monthsBack,
        isDerivative,
        fromDate,
        toDate,
      ),
    enabled: !!projectId,
  });
};
